import { AlertCircle, CheckIcon, CrownIcon, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import { cn } from "../lib/utils";
import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

interface PremiumDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  highlightPro?: boolean;
}

const PremiumDialog: React.FC<PremiumDialogProps> = ({
  isOpen,
  onOpenChange,
  onSuccess,
  highlightPro = false,
}) => {
  const { premiumType } = useAuth();
  const {
    loading: stripeLoading,
    error: stripeError,
    subscribeBasic: stripeSubscribeBasic,
    subscribePro: stripeSubscribePro,
  } = useStripe();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const isBasic = premiumType === "basic";
  const { subscribeBasic, subscribePro } = useAuth();

  const handleSubscribeBasic = async () => {
    try {
      setIsLoading("basic");
      setLocalError(null);

      // Usar directamente el método del contexto de Stripe
      const checkoutUrl = await stripeSubscribeBasic();
      console.log("Redirigiendo a Stripe Checkout:", checkoutUrl);

      // Redirigir al usuario al checkout de Stripe
      if (checkoutUrl) {
        console.log("Iniciando redirección a:", checkoutUrl);
        window.location.assign(checkoutUrl);
      } else {
        throw new Error("No se recibió una URL de checkout válida");
      }

      // Note: We don't close the dialog here because the user will be redirected to Stripe
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Error al suscribirse al plan Basic:", error);
      setLocalError(
        error.message ||
          "No se pudo procesar la suscripción. Inténtalo más tarde."
      );
      setIsLoading(null);
    }
  };

  const handleSubscribePro = async () => {
    try {
      setIsLoading("pro");
      setLocalError(null);

      // Usar directamente el método del contexto de Stripe
      const checkoutUrl = await stripeSubscribePro();
      console.log("Redirigiendo a Stripe Checkout:", checkoutUrl);

      // Redirigir al usuario al checkout de Stripe
      if (checkoutUrl) {
        console.log("Iniciando redirección a:", checkoutUrl);
        window.location.assign(checkoutUrl);
      } else {
        throw new Error("No se recibió una URL de checkout válida");
      }

      // Note: We don't close the dialog here because the user will be redirected to Stripe
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Error al suscribirse al plan Pro:", error);
      setLocalError(
        error.message ||
          "No se pudo procesar la suscripción. Inténtalo más tarde."
      );
      setIsLoading(null);
    }
  };

  // Mostrar mensaje de error si existe
  const errorMessage = localError || stripeError;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Solo permitir cerrar el diálogo si no hay operaciones en curso
        if (!isLoading) {
          onOpenChange(open);
        }
      }}
    >
      <DialogContent
        className={cn(
          "sm:max-w-xl",
          "animate-in fade-in-0 zoom-in-95 data-[state=open]:slide-in-from-center data-[state=closed]:slide-out-to-center",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {isBasic && highlightPro
              ? "Actualiza a Premium Pro"
              : "Funcionalidad Premium"}
          </DialogTitle>
          <DialogDescription>
            {isBasic && highlightPro
              ? "Actualiza tu plan para acceder a todas las funcionalidades avanzadas."
              : "Desbloquea todas las funcionalidades avanzadas con una suscripción premium."}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {stripeLoading ? (
          <div className="py-8 flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Cargando opciones de pago...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Plan Basic */}
            <div
              className={cn(
                "border rounded-lg p-4 flex flex-col",
                isBasic && "opacity-60"
              )}
            >
              <h3 className="text-lg font-semibold mb-2">Plan Basic</h3>
              <div className="text-xl font-bold mb-4">€15/mes</div>
              <ul className="space-y-2 flex-grow">
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Filtros avanzados</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Exportación básica (hasta 20 documentos)</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Estadísticas básicas</span>
                </li>
              </ul>
              <Button
                className="mt-4 w-full"
                onClick={handleSubscribeBasic}
                disabled={isBasic || isLoading !== null}
              >
                {isLoading === "basic" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Procesando...
                  </>
                ) : isBasic ? (
                  "Plan Actual"
                ) : (
                  "Suscribirse (Basic)"
                )}
              </Button>
            </div>

            {/* Plan Pro */}
            <div
              className={cn(
                "border rounded-lg p-4 flex flex-col",
                (highlightPro || isBasic) &&
                  "border-primary bg-primary/5 shadow-md"
              )}
            >
              {(highlightPro || isBasic) && (
                <div className="absolute -top-3 -right-3 bg-primary text-primary-foreground rounded-full p-1">
                  <CrownIcon className="h-5 w-5" />
                </div>
              )}
              <h3 className="text-lg font-semibold mb-2">Plan Pro</h3>
              <div className="text-xl font-bold mb-4">€30/mes</div>
              <ul className="space-y-2 flex-grow">
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Todas las funciones del plan Basic</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Exportación avanzada (hasta 50 documentos)</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Estadísticas avanzadas y gráficos</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Búsqueda en texto completo</span>
                </li>
                <li className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span>Filtros guardados</span>
                </li>
              </ul>
              <Button
                className="mt-4 w-full"
                variant="default"
                onClick={handleSubscribePro}
                disabled={isLoading !== null}
              >
                {isLoading === "pro" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Procesando...
                  </>
                ) : isBasic ? (
                  "Actualizar a Pro"
                ) : (
                  "Suscribirse (Pro)"
                )}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading !== null}
          >
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumDialog;
